/** Components for `./useSubjectOpener`, split out so that module can stay a plain `.ts`. */
import * as React from "react";
import { AddressDisplayProvider } from "@authe/ui-core";
import type { FocusTarget } from "@authe/fe-commons/focus";
import { GraphContextOpenerContext } from "@authe/tx-graph";
import type { InspectorController } from "./useInspector";
import { useContextLinkOpener, useSubjectOpener, InspectorHostContext } from "./useSubjectOpener";

export function InspectorHost({
  inspector,
  children,
}: {
  inspector: InspectorController;
  children: React.ReactNode;
}): React.ReactElement {
  return <InspectorHostContext.Provider value={inspector}>
    <InspectorAddressDefaults><GraphContextDefaults>{children}</GraphContextDefaults></InspectorAddressDefaults>
  </InspectorHostContext.Provider>;
}

/**
 * Graph context chips ("Actor · …", a situation) open through the page's
 * opener, like every other subject on it. Mounted by {@link InspectorHost} for
 * a page with a panel, and once at the root so a graph on any other page
 * still opens a chip's page inside the app rather than reloading it.
 */
export function GraphContextDefaults({ children }: { children: React.ReactNode }): React.ReactElement {
  const open = useContextLinkOpener();
  return <GraphContextOpenerContext.Provider value={open}>{children}</GraphContextOpenerContext.Provider>;
}

/** Shared chips inside a page use that page's inspector, like AddressLink. */
function InspectorAddressDefaults({ children }: { children: React.ReactNode }): React.ReactElement {
  const { openAddress } = useSubjectOpener();
  const onDrilldown = React.useCallback((target: FocusTarget) => {
    if (target.kind === "address") openAddress(target.id, target.chain);
  }, [openAddress]);
  return <AddressDisplayProvider onDrilldown={onDrilldown}>{children}</AddressDisplayProvider>;
}
